import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  User, Calendar, Briefcase, Building2, Edit, 
  FileText, ClipboardList, FolderOpen, AlertTriangle,
  Inbox, FileWarning, Clock, MapPin, Archive, FileBarChart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEPARTMENTS = ['Warehouse', 'Operations', 'drivers', 'Admin', 'Sales', 'Customer Service'];

const EmployeeProfile = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showBookTimeOffDialog, setShowBookTimeOffDialog] = useState(false);
  const [showRegisterSickDialog, setShowRegisterSickDialog] = useState(false);
  const [showRegisterAbsentDialog, setShowRegisterAbsentDialog] = useState(false);
  const [showBookOtherLeaveDialog, setShowBookOtherLeaveDialog] = useState(false);
  const [showLogIncidentDialog, setShowLogIncidentDialog] = useState(false);
  const [showIncidentDetailsDialog, setShowIncidentDetailsDialog] = useState(false);
  const [showUploadDocumentDialog, setShowUploadDocumentDialog] = useState(false);
  const [showViewDocumentDialog, setShowViewDocumentDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentCategory, setDocumentCategory] = useState('employment_essentials');
  const [employmentSubTab, setEmploymentSubTab] = useState('contracts');
  const [performanceSubTab, setPerformanceSubTab] = useState('PIP');
  const [financialSubTab, setFinancialSubTab] = useState('payslips');
  const [trainingSubTab, setTrainingSubTab] = useState('training');
  const [documentsSubTab, setDocumentsSubTab] = useState('miscellaneous');
  const [showViewBox, setShowViewBox] = useState(false);
  const [viewMode, setViewMode] = useState(''); // 'signin', 'incidents', 'attendance', 'documents'
  const [signInRecords, setSignInRecords] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('Contract');
  const [managers, setManagers] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [ongoingAbsence, setOngoingAbsence] = useState(null);
  const [showReturnToWorkDialog, setShowReturnToWorkDialog] = useState(false);
  const [rtwRecords, setRtwRecords] = useState([]);
  const [showViewRtwDialog, setShowViewRtwDialog] = useState(false);
  const [selectedRtwRecord, setSelectedRtwRecord] = useState(null);
  const [loadingRtwRecords, setLoadingRtwRecords] = useState(false);
  const [rtwFormData, setRtwFormData] = useState({
    return_to_work_date: '',
    rtw_reason: '',
    rtw_discussion_date: '',
    medical_certification: false,
    discussion_summary: '',
    other_comments: ''
  });
  // Per-day breakdown shown in the RTW dialog. One entry per working day in the
  // absence period; each row is independently reclassifiable to any Sick/Absent code.
  const [rtwDailyEntries, setRtwDailyEntries] = useState([]);
  const [rtwEligibleTypes, setRtwEligibleTypes] = useState([]);
  const [rtwPreviewLoading, setRtwPreviewLoading] = useState(false);
  const [rtwPreviewError, setRtwPreviewError] = useState('');
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [bradfordFactor, setBradfordFactor] = useState(null);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [showGenerateReportDialog, setShowGenerateReportDialog] = useState(false);
  const [reportDateRange, setReportDateRange] = useState('month_to_date');
  const [customReportDates, setCustomReportDates] = useState({ start: '', end: '' });
  const [generatingReport, setGeneratingReport] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  // Leave allowance state
  const [leaveAllowances, setLeaveAllowances] = useState([]);
  const [loadingAllowances, setLoadingAllowances] = useState(false);
  const [showAddExtraDialog, setShowAddExtraDialog] = useState(false);
  const [selectedAllowanceYear, setSelectedAllowanceYear] = useState(new Date().getFullYear());
  const [extraAllowanceData, setExtraAllowanceData] = useState({
    type: 'charity',
    reason: '',
    days: 1
  });
  const [timeOffData, setTimeOffData] = useState({
    first_day: '',
    first_day_type: 'full_day',
    last_day: '',
    last_day_type: 'full_day',
    notes: ''
  });
  const [sicknessData, setSicknessData] = useState({
    date: '',
    absence_type_code: 'S',
    notes: ''
  });
  const [absentData, setAbsentData] = useState({
    date: '',
    absence_type_code: 'UA',
    notes: ''
  });
  const [otherLeaveData, setOtherLeaveData] = useState({
    date: '',
    absence_type_code: 'C',
    notes: ''
  });
  const [incidentData, setIncidentData] = useState({
    category: 'Disciplinary',
    short_summary: '',
    detailed_explanation: '',
    incident_date: '',
    incident_time: '',
    managers_involved: [],
    outcome_summary: '',
    outcome_actions: '',
    attachment_url: '',
    attachment_file: null
  });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    email: '',
    holiday_entitlement_days: 28,
    contracted_hours_per_week: 40,
    default_start_time: '09:00',
    default_finish_time: '17:00',
    department: 'Warehouse',
    job_title: '',
    pin: '',
    is_manager: false,
    managed_departments: []
  });

  useEffect(() => {
    fetchEmployeeData();
    fetchAbsenceTypes();
    fetchSignInRecords();
    fetchIncidents();
    fetchManagers();
    fetchDocuments();
    fetchBradfordFactor();
    fetchTimeOffRequests();
    fetchOngoingAbsence();
    fetchRtwRecords(); // CRITICAL: Load sickness records for Days Booked calculation
    fetchLeaveAllowances();
  }, [employeeId]);

  useEffect(() => {
    console.log('=== Dialog State Changed ===');
    console.log('showEditDialog:', showEditDialog);
    console.log('employee.profile_image exists:', !!employee?.profile_image);
    
    // When dialog opens, set the preview to the current saved image
    if (showEditDialog && employee?.profile_image) {
      console.log('Setting preview to saved image');
      setProfileImagePreview(employee.profile_image);
    }
    // When dialog closes, clear the preview states
    if (!showEditDialog) {
      console.log('Clearing preview states');
      setProfileImage(null);
      setProfileImagePreview(null);
    }
  }, [showEditDialog, employee?.profile_image]);

  const fetchOngoingAbsence = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/sickness/ongoing/${employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.has_ongoing_absence) {
          setOngoingAbsence(data.absence);
          // Pre-fill RTW form data
          setRtwFormData(prev => ({
            ...prev,
            rtw_discussion_date: new Date().toISOString().split('T')[0]
          }));
        } else {
          setOngoingAbsence(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch ongoing absence:', error);
    }
  };

  const fetchAbsenceTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/absence-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const types = await response.json();
        setAbsenceTypes(types);
      }
    } catch (error) {
      console.error('Failed to fetch absence types:', error);
    }
  };

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      console.log('=== Fetching Employee Data ===');
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch employees');

      const employees = await response.json();
      const emp = employees.find(e => e.id === employeeId);
      
      if (!emp) {
        toast.error('Employee not found');
        navigate('/admin/employees');
        return;
      }

      console.log('Employee data received:', {
        name: `${emp.first_name} ${emp.last_name}`,
        has_profile_image: !!emp.profile_image,
        profile_image_preview: emp.profile_image?.substring(0, 50) + '...'
      });

      setEmployee(emp);
      setFormData({
        first_name: emp.first_name || '',
        last_name: emp.last_name || '',
        date_of_birth: emp.date_of_birth || '',
        email: emp.email || '',
        holiday_entitlement_days: emp.holiday_entitlement_days || 28,
        contracted_hours_per_week: emp.contracted_hours_per_week || 40,
        default_start_time: emp.default_start_time || '09:00',
        default_finish_time: emp.default_finish_time || '17:00',
        department: emp.department || 'Warehouse',
        job_title: emp.job_title || '',
        pin: emp.pin || '',
        is_manager: emp.is_manager || false,
        managed_departments: emp.managed_departments || [],
        profile_image: emp.profile_image || ''
      });
      console.log('FormData updated with profile_image:', !!emp.profile_image);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const toggleDepartment = (dept) => {
    setFormData(prev => ({
      ...prev,
      managed_departments: prev.managed_departments.includes(dept)
        ? prev.managed_departments.filter(d => d !== dept)
        : [...prev.managed_departments, dept]
    }));
  };

  const handleUpdateEmployee = async () => {
    try {
      console.log('=== STARTING UPDATE ===');
      console.log('profileImage state:', profileImage);
      console.log('profileImagePreview state:', profileImagePreview);
      
      const token = localStorage.getItem('token');
      
      // If there's a new profile image, upload it first
      let updatedFormData = { ...formData };
      console.log('Initial formData:', formData);
      
      if (profileImage) {
        console.log('Uploading image...');
        const formDataImg = new FormData();
        formDataImg.append('file', profileImage);
        
        const imageResponse = await fetch(`${BACKEND_URL}/api/employees/${employeeId}/upload-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataImg
        });
        
        console.log('Image upload response status:', imageResponse.status);
        
        if (imageResponse.ok) {
          const responseData = await imageResponse.json();
          console.log('Image upload response:', responseData);
          updatedFormData.profile_image = responseData.image_url;
          console.log('Updated formData with image_url:', updatedFormData.profile_image?.substring(0, 50) + '...');
        } else {
          console.error('Image upload failed:', await imageResponse.text());
        }
      } else {
        console.log('No profileImage to upload');
      }
      
      // Update employee data
      console.log('Sending PUT request with data:', { ...updatedFormData, profile_image: updatedFormData.profile_image ? 'BASE64_DATA' : undefined });
      const response = await fetch(`${BACKEND_URL}/api/employees/${employeeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedFormData)
      });

      console.log('PUT response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PUT failed:', errorText);
        throw new Error('Failed to update employee');
      }

      toast.success('Employee updated successfully');
      setShowEditDialog(false);
      setProfileImage(null);
      setProfileImagePreview(null);
      
      console.log('Fetching updated employee data...');
      await fetchEmployeeData();
      console.log('Employee data refreshed');
      
      // CRITICAL: Refetch leave allowances when employee settings change
      // This ensures that changes to holiday_entitlement_days are reflected
      console.log('Refetching leave allowances after employee update...');
      await fetchLeaveAllowances();
      console.log('Leave allowances refreshed');
      
      // If user updated their own profile image, refresh the page to update sidebar
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (currentUser.id === employeeId && updatedFormData.profile_image) {
        console.log('Updating localStorage and reloading page...');
        currentUser.profile_image = updatedFormData.profile_image;
        localStorage.setItem('user', JSON.stringify(currentUser));
        window.location.reload();
      }
      
      console.log('=== UPDATE COMPLETE ===');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update employee');
    }
  };



  const fetchRtwRecords = async () => {
    setLoadingRtwRecords(true);
    try {
      const token = localStorage.getItem('token');
      // Fetch ALL sickness/absence records (not just completed ones)
      const response = await fetch(`${BACKEND_URL}/api/sickness/records/${employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const records = await response.json();
        setRtwRecords(records);
      }
    } catch (error) {
      console.error('Failed to fetch attendance records:', error);
      toast.error('Failed to load attendance records');
    } finally {
      setLoadingRtwRecords(false);
    }
  };

  const handleBookTimeOff = async () => {
    if (!timeOffData.first_day || !timeOffData.last_day) {
      toast.error('Please select both start and end dates');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/time-off-requests/manager-book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_id: employeeId,
          ...timeOffData
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to book time off');
      }

      const result = await response.json();
      const yearText = result.allowance_year ? ` for ${result.allowance_year}` : '';
      toast.success(`Time off booked successfully! ${result.total_days} day(s) booked. ${result.remaining_allowance} days remaining${yearText}.`);
      setShowBookTimeOffDialog(false);
      setTimeOffData({
        first_day: '',
        first_day_type: 'full_day',
        last_day: '',
        last_day_type: 'full_day',
        notes: ''
      });
      fetchEmployeeData(); // Refresh employee data to show updated allowance
    } catch (error) {
      toast.error(error.message || 'Failed to book time off');
    }
  };

  const handleRegisterSick = async () => {
    if (!sicknessData.date) {
      toast.error('Please select a date');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/sickness/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_id: employeeId,
          ...sicknessData
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to register sickness');
      }

      const result = await response.json();
      toast.success(`Sickness registered successfully! ${result.absence_type}. Employee marked as currently absent.`);
      setShowRegisterSickDialog(false);
      setSicknessData({
        date: '',
        absence_type_code: 'S',
        notes: ''
      });
      fetchEmployeeData(); // Refresh employee data
      fetchOngoingAbsence(); // Refresh ongoing absence to update button
    } catch (error) {
      toast.error(error.message || 'Failed to register sickness');
    }
  };


  // Pull the per-day skeleton for the RTW dialog whenever the user changes
  // the last-day-absent date. Server returns one row per working day (excludes
  // weekends + bank holidays) plus the catalogue of eligible Sick/Absent codes.
  const fetchRtwPreview = async (lastDay) => {
    if (!ongoingAbsence || !lastDay) {
      setRtwDailyEntries([]);
      setRtwEligibleTypes([]);
      setRtwPreviewError('');
      return;
    }
    setRtwPreviewLoading(true);
    setRtwPreviewError('');
    try {
      const token = localStorage.getItem('token');
      const url = `${BACKEND_URL}/api/sickness/rtw-preview/${ongoingAbsence.id}?last_day=${encodeURIComponent(lastDay)}`;
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to build RTW preview');
      }
      const data = await response.json();
      setRtwDailyEntries(data.entries || []);
      setRtwEligibleTypes(data.eligible_absence_types || []);
    } catch (error) {
      setRtwDailyEntries([]);
      setRtwEligibleTypes([]);
      setRtwPreviewError(error.message || 'Failed to build RTW preview');
    } finally {
      setRtwPreviewLoading(false);
    }
  };

  // When the manager picks a different code for a specific day, snap the
  // days_deducted to the rate of the new code so the live total updates.
  const updateRtwDailyEntry = (date, newCode) => {
    setRtwDailyEntries(prev => prev.map(entry => {
      if (entry.date !== date) return entry;
      const atype = rtwEligibleTypes.find(t => t.code === newCode);
      return {
        ...entry,
        absence_type_code: newCode,
        days_deducted: atype ? Number(atype.days_deducted || 0) : 0,
      };
    }));
  };

  const handleReturnToWork = async () => {
    if (!rtwFormData.return_to_work_date || !rtwFormData.rtw_reason || !rtwFormData.rtw_discussion_date || !rtwFormData.discussion_summary) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!rtwDailyEntries || rtwDailyEntries.length === 0) {
      toast.error('No working days found in the chosen period — pick a valid last day absent');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/sickness/return-to-work`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          absence_record_id: ongoingAbsence.id,
          ...rtwFormData,
          daily_entries: rtwDailyEntries.map(e => ({
            date: e.date,
            absence_type_code: e.absence_type_code,
          })),
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to complete Return to Work');
      }

      const result = await response.json();
      const deductedMsg = result.allowance_deducted > 0
        ? ` ${result.allowance_deducted} day(s) deducted from annual leave.`
        : '';
      toast.success(`Return to Work completed! ${result.total_days} working day(s) recorded.${deductedMsg}`);
      setShowReturnToWorkDialog(false);
      setRtwFormData({
        return_to_work_date: '',
        rtw_reason: '',
        rtw_discussion_date: '',
        medical_certification: false,
        discussion_summary: '',
        other_comments: ''
      });
      setRtwDailyEntries([]);
      setRtwEligibleTypes([]);
      setRtwPreviewError('');
      await Promise.all([
        fetchEmployeeData(),
        fetchTimeOffRequests(),
        fetchRtwRecords(),
        fetchOngoingAbsence(),
        fetchBradfordFactor()
      ]);
    } catch (error) {
      toast.error(error.message || 'Failed to complete Return to Work');
    }
  };

  const calculateWorkingDays = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return 0;
    
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    
    if (end < start) return 0;
    
    let workingDays = 0;
    let currentDate = new Date(start);
    
    while (currentDate <= end) {
      // Monday = 1, Sunday = 0
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not Saturday or Sunday
        workingDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return workingDays;
  };

  const handleRegisterAbsent = async () => {
    if (!absentData.date) {
      toast.error('Please select a date');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/sickness/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_id: employeeId,
          ...absentData
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to register absence');
      }

      const result = await response.json();
      toast.success(`Absence registered successfully! ${result.absence_type}. Employee marked as currently absent.`);
      setShowRegisterAbsentDialog(false);
      setAbsentData({
        date: '',
        absence_type_code: 'UA',
        notes: ''
      });
      fetchEmployeeData(); // Refresh employee data
      fetchOngoingAbsence(); // Refresh ongoing absence to update button
    } catch (error) {
      toast.error(error.message || 'Failed to register absence');
    }
  };

  const handleBookOtherLeave = async () => {
    if (!otherLeaveData.date) {
      toast.error('Please select a date');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/sickness/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_id: employeeId,
          ...otherLeaveData
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to book other leave');
      }

      const result = await response.json();
      const deductionMsg = result.days_deducted > 0 
        ? ` ${result.days_deducted} days deducted. ${result.remaining_allowance} days remaining.` 
        : '';
      
      // Different message based on whether RTW is required
      if (result.requires_rtw === false) {
        toast.success(`${result.absence_type} recorded successfully.${deductionMsg}`);
      } else {
        toast.success(`Other leave booked successfully! ${result.absence_type}.${deductionMsg}`);
      }
      
      setShowBookOtherLeaveDialog(false);
      setOtherLeaveData({
        date: '',
        absence_type_code: 'C',
        notes: ''
      });
      fetchEmployeeData(); // Refresh employee data
      fetchOngoingAbsence(); // Refresh ongoing absence to update button
    } catch (error) {
      toast.error(error.message || 'Failed to book other leave');
    }
  };

  const fetchSignInRecords = async () => {
    setLoadingRecords(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/timelogs/employee/${employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch sign-in records');
      }
      
      const records = await response.json();
      setSignInRecords(records);
    } catch (error) {
      console.error('Failed to load sign-in records:', error);
      setSignInRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const fetchIncidents = async () => {
    setLoadingIncidents(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/incidents/employee/${employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch incidents');
      }
      
      const data = await response.json();
      setIncidents(data);
    } catch (error) {
      console.error('Failed to load incidents:', error);
      setIncidents([]);
    } finally {
      setLoadingIncidents(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/managers/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setManagers(data);
      }
    } catch (error) {
      console.error('Failed to load managers:', error);
    }
  };

  const handleLogIncident = async () => {
    if (!incidentData.short_summary || !incidentData.incident_date || !incidentData.incident_time || !incidentData.outcome_summary) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let attachment_url = incidentData.attachment_url || '';

      // Upload file if one was selected
      if (incidentData.attachment_file) {
        const formData = new FormData();
        formData.append('file', incidentData.attachment_file);

        const uploadResponse = await fetch(`${BACKEND_URL}/api/incidents/upload-attachment`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload attachment');
        }

        const uploadResult = await uploadResponse.json();
        attachment_url = uploadResult.attachment_url;
      }

      // Create incident with attachment URL
      const response = await fetch(`${BACKEND_URL}/api/incidents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_id: employeeId,
          ...incidentData,
          attachment_url,
          attachment_file: undefined // Remove file object from JSON
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to log incident');
      }

      toast.success('Incident logged successfully');
      setShowLogIncidentDialog(false);
      setIncidentData({
        category: 'Disciplinary',
        short_summary: '',
        detailed_explanation: '',
        incident_date: '',
        incident_time: '',
        managers_involved: [],
        outcome_summary: '',
        outcome_actions: '',
        attachment_url: '',
        attachment_file: null
      });
      // Clear file input
      const fileInput = document.getElementById('attachment_file');
      if (fileInput) fileInput.value = '';
      fetchIncidents(); // Refresh list
    } catch (error) {
      toast.error(error.message || 'Failed to log incident');
    }
  };

  const handleResolveIncident = async (incidentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/incidents/${incidentId}/resolve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to update incident');
      }

      const result = await response.json();
      toast.success(result.message);
      fetchIncidents();
    } catch (error) {
      toast.error(error.message || 'Failed to update incident');
    }
  };

  const handleDeleteIncident = async (incidentId) => {
    if (!window.confirm('Are you sure you want to delete this incident? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/incidents/${incidentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete incident');
      }

      toast.success('Incident deleted successfully');
      fetchIncidents();
    } catch (error) {
      toast.error(error.message || 'Failed to delete incident');
    }
  };

  const handleViewIncidentDetails = async (incident) => {
    setSelectedIncident(incident);
    setShowIncidentDetailsDialog(true);
  };

  const fetchDocuments = async () => {
    setLoadingDocuments(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/documents/employee/${employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }
      
      const data = await response.json();
      setDocuments(data);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('employee_id', employeeId);
      formData.append('category', uploadCategory);

      const response = await fetch(`${BACKEND_URL}/api/documents/upload?employee_id=${employeeId}&category=${encodeURIComponent(uploadCategory)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload document');
      }

      toast.success('Document uploaded successfully');
      setShowUploadDocumentDialog(false);
      setSelectedFile(null);
      setUploadCategory('Contract');
      fetchDocuments();
    } catch (error) {
      toast.error(error.message || 'Failed to upload document');
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/documents/${documentId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(error.message || 'Failed to download document');
    }
  };

  const handleArchiveEmployee = async () => {
    if (!window.confirm('Are you sure you want to archive this employee? Their data will be preserved but they will not appear in active employee lists.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees/${employeeId}/archive`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to archive employee');
      }

      toast.success('Employee archived successfully');
      navigate('/admin/employees');
    } catch (error) {
      toast.error('Failed to archive employee');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      toast.error(error.message || 'Failed to delete document');
    }
  };

  const handleViewDocument = async (document) => {
    setSelectedDocument(document);
    setShowViewDocumentDialog(true);
  };

  const fetchBradfordFactor = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/bradford-factor/employee/${employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBradfordFactor(data);
      }
    } catch (error) {
      console.error('Failed to load Bradford Factor:', error);
    }
  };

  const fetchTimeOffRequests = async () => {
    if (!employeeId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees/${employeeId}/time-off-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTimeOffRequests(data);
      }
    } catch (error) {
      console.error('Failed to load time-off requests:', error);
    }
  };

  const fetchLeaveAllowances = async () => {
    if (!employeeId) return;
    setLoadingAllowances(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/leave-allowances/${employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeaveAllowances(data);
      }
    } catch (error) {
      console.error('Failed to load leave allowances:', error);
    }
    setLoadingAllowances(false);
  };

  const handleAddExtraAllowance = async () => {
    if (!extraAllowanceData.reason.trim()) {
      toast.error('Please enter a reason for the extra days');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/leave-allowances/${employeeId}/${selectedAllowanceYear}/extra`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(extraAllowanceData)
        }
      );
      
      if (response.ok) {
        toast.success(`Added ${extraAllowanceData.days} extra day(s)`);
        setShowAddExtraDialog(false);
        setExtraAllowanceData({ type: 'charity', reason: '', days: 1 });
        fetchLeaveAllowances();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add extra allowance');
      }
    } catch (error) {
      console.error('Error adding extra allowance:', error);
      toast.error('Failed to add extra allowance');
    }
  };

  const handleRemoveExtraAllowance = async (year, extraId) => {
    if (!window.confirm('Are you sure you want to remove this extra allowance?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/leave-allowances/${employeeId}/${year}/extra/${extraId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Extra allowance removed');
        fetchLeaveAllowances();
      } else {
        toast.error('Failed to remove extra allowance');
      }
    } catch (error) {
      toast.error('Failed to remove extra allowance');
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        employee_id: employeeId,
        date_range: reportDateRange
      });

      if (reportDateRange === 'custom' && customReportDates.start && customReportDates.end) {
        params.append('start_date', customReportDates.start);
        params.append('end_date', customReportDates.end);
      }

      const response = await fetch(`${BACKEND_URL}/api/employees/${employeeId}/generate-report?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${employee.first_name}_${employee.last_name}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Report generated and downloaded successfully');
        setShowGenerateReportDialog(false);
        fetchDocuments(); // Refresh documents to show new report
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const calculateDaysBooked = () => {
    // Use yearly allowance data for current year if available
    const currentYear = new Date().getFullYear();
    const currentYearAllowance = leaveAllowances.find(a => a.year === currentYear);
    
    if (currentYearAllowance) {
      return currentYearAllowance.days_used || 0;
    }
    
    // Fallback: Calculate from time-off requests for current year only
    let total = 0;
    const yearStart = `${currentYear}-01-01`;
    const yearEnd = `${currentYear}-12-31`;
    
    // Add approved time-off requests (regular holidays) for current year only
    if (timeOffRequests && timeOffRequests.length > 0) {
      const approvedRequests = timeOffRequests.filter(req => 
        req.status === 'approved' && 
        req.first_day >= yearStart && 
        req.first_day <= yearEnd
      );
      total += approvedRequests.reduce((sum, req) => sum + (req.total_days || 0), 0);
    }
    
    // Add sickness holiday deductions (completed sickness with allowance deduction) for current year
    if (rtwRecords && rtwRecords.length > 0) {
      const sicknessHolidays = rtwRecords.filter(record => {
        const recordYear = record.created_at ? new Date(record.created_at).getFullYear() : null;
        return record.status === 'completed' && 
               (record.total_allowance_deduction || 0) > 0 &&
               recordYear === currentYear;
      });
      total += sicknessHolidays.reduce((sum, record) => sum + (record.total_allowance_deduction || 0), 0);
    }
    
    return total;
  };

  const calculateDaysLeft = () => {
    // Use yearly allowance data for current year if available
    const currentYear = new Date().getFullYear();
    const currentYearAllowance = leaveAllowances.find(a => a.year === currentYear);
    
    if (currentYearAllowance) {
      return currentYearAllowance.days_remaining || 0;
    }
    
    // Fallback: Calculate from employee entitlement
    const entitlement = employee?.holiday_entitlement_days || 28;
    return entitlement - calculateDaysBooked();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-teal-600 text-xl">Loading profile...</div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Employee not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="text-teal-700 hover:text-teal-900"
        >
          ← Back
        </Button>
      </div>

      {/* Profile Header - Mobile Responsive */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
          {/* Profile Picture */}
          <div className="w-20 h-20 sm:w-32 sm:h-32 flex-shrink-0 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white text-2xl sm:text-4xl font-bold shadow-lg overflow-hidden">
            {employee.profile_image ? (
              <img 
                src={employee.profile_image} 
                alt={`${employee.first_name} ${employee.last_name}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <span>{employee.first_name?.[0]}{employee.last_name?.[0]}</span>
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-1 w-full min-w-0">
            <div className="space-y-4">
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2">
                  {employee.first_name} {employee.last_name}
                </h1>
                
                <div className="space-y-1 sm:space-y-2">
                  <div className="flex items-center gap-2 text-gray-600 text-xs sm:text-base">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="truncate">DOB: {employee.date_of_birth ? new Date(employee.date_of_birth).toLocaleDateString('en-GB') : 'Not set'}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-600 text-xs sm:text-base">
                    <Building2 className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="truncate">Department: {employee.department || 'Not assigned'}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-600 text-xs sm:text-base">
                    <Briefcase className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="truncate">Job Role: {employee.job_title || 'Not set'}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => {
                    setShowEditDialog(true);
                  }}
                  className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-sm"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Settings
                </Button>
                <Button
                  onClick={handleArchiveEmployee}
                  variant="outline"
                  className="w-full sm:w-auto border-orange-500 text-orange-600 hover:bg-orange-50 text-sm"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Currently Absent Badge */}
      {ongoingAbsence && (
        <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-red-500 rounded-full p-2">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-900">Currently Absent</h3>
              <p className="text-sm text-red-700">
                {ongoingAbsence.absence_type_name || 'Sickness'} since {new Date(ongoingAbsence.first_day_absent).toLocaleDateString('en-GB')}
              </p>
              {ongoingAbsence.notes && (
                <p className="text-xs text-red-600 mt-1 italic">Note: {ongoingAbsence.notes}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats Tiles - Mobile Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border-l-4 border-red-500">
          <p className="text-gray-600 text-xs sm:text-sm font-semibold mb-1 sm:mb-2">Bradford Factor</p>
          <p className="text-2xl sm:text-4xl font-bold text-gray-900">
            {bradfordFactor ? bradfordFactor.bradford_factor : '...'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {bradfordFactor ? `${bradfordFactor.instances} instances • ${bradfordFactor.total_days} days` : 'Loading...'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border-l-4 border-blue-500">
          <p className="text-gray-600 text-xs sm:text-sm font-semibold mb-1 sm:mb-2">Days Booked ({new Date().getFullYear()})</p>
          <p className="text-2xl sm:text-4xl font-bold text-gray-900" data-testid="days-booked-value">{calculateDaysBooked()}</p>
          <p className="text-xs text-gray-500 mt-1">Holiday taken this year</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border-l-4 border-green-500">
          <p className="text-gray-600 text-xs sm:text-sm font-semibold mb-1 sm:mb-2">Days Left ({new Date().getFullYear()})</p>
          <p className="text-2xl sm:text-4xl font-bold text-gray-900">{calculateDaysLeft()}</p>
          <p className="text-xs text-gray-500 mt-1">Remaining this year</p>
        </div>
      </div>

      {/* Action Sections - Mobile Responsive */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        {/* Time Off Section */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
            Time Off
          </h3>
          <div className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start text-sm" 
              onClick={() => setShowBookTimeOffDialog(true)}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book time off
            </Button>
            
            {ongoingAbsence ? (
              <Button 
                variant="outline" 
                className="w-full justify-start bg-green-50 border-green-500 text-green-700 hover:bg-green-100" 
                onClick={() => setShowReturnToWorkDialog(true)}
              >
                <Clock className="w-4 h-4 mr-2" />
                Return to Work
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-sm" 
                  onClick={() => setShowRegisterSickDialog(true)}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Register Sick
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-sm" 
                  onClick={() => setShowRegisterAbsentDialog(true)}
                >
                  <FileWarning className="w-4 h-4 mr-2" />
                  Register Absent
                </Button>
              </>
            )}
            
            <Button 
              variant="outline" 
              className="w-full justify-start text-sm" 
              onClick={() => setShowBookOtherLeaveDialog(true)}
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Book other leave
            </Button>
          </div>
        </div>

        {/* View Section */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
            <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            View
          </h3>
          <div className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start text-sm" 
              onClick={() => {
                setViewMode('signin');
                setShowViewBox(true);
                setTimeout(() => {
                  document.getElementById('view-box-section')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            >
              <Clock className="w-4 h-4 mr-2" />
              Sign in records
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start text-sm" 
              onClick={() => {
                setViewMode('attendance');
                setShowViewBox(true);
                fetchRtwRecords();
                setTimeout(() => {
                  document.getElementById('view-box-section')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Attendance
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start text-sm" 
              onClick={() => {
                setViewMode('documents');
                setShowViewBox(true);
                setTimeout(() => {
                  document.getElementById('view-box-section')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            >
              <FileText className="w-4 h-4 mr-2" />
              Documents
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start text-sm" 
              onClick={() => {
                setViewMode('incidents');
                setShowViewBox(true);
                setTimeout(() => {
                  document.getElementById('view-box-section')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            >
              <FileWarning className="w-4 h-4 mr-2" />
              Incidents
            </Button>
          </div>
        </div>

        {/* HR Section */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            HR
          </h3>
          <div className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start text-sm" 
              onClick={() => setShowLogIncidentDialog(true)}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Log Incident
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start text-sm" 
              onClick={() => setShowGenerateReportDialog(true)}
            >
              <FileBarChart className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start text-sm" 
              onClick={() => setShowUploadDocumentDialog(true)}
            >
              <FileText className="w-4 h-4 mr-2" />
              Upload Documents
            </Button>
          </div>
        </div>
      </div>

      {/* Yearly Leave Allowances Section */}
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
            Yearly Leave Allowances
          </h3>
          <Button
            size="sm"
            onClick={() => {
              setSelectedAllowanceYear(new Date().getFullYear());
              setShowAddExtraDialog(true);
            }}
            className="bg-teal-600 hover:bg-teal-700"
          >
            + Add Extra Days
          </Button>
        </div>
        
        {loadingAllowances ? (
          <p className="text-gray-500 text-center py-4">Loading allowances...</p>
        ) : leaveAllowances.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No yearly allowance records found</p>
        ) : (
          <div className="space-y-4">
            {leaveAllowances.slice(0, 3).map(allowance => (
              <div key={allowance.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-900">{allowance.year}</span>
                    {allowance.is_pro_rated && (
                      <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">Pro-rated</span>
                    )}
                    {allowance.year_end_status === 'processed' && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">Year-End Processed</span>
                    )}
                  </div>
                  <div className={`text-lg font-bold ${allowance.days_remaining > 0 ? 'text-amber-600' : 'text-gray-600'}`}>
                    {allowance.days_remaining} days remaining
                  </div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-gray-500 text-xs">Base Bookable</div>
                    <div className="font-semibold">{allowance.base_allowance} days</div>
                    {allowance.contractual_days && (
                      <div className="text-xs text-gray-400 mt-1">
                        ({allowance.contractual_days} - {allowance.bank_holidays_deducted || 8} BH + {allowance.long_service_bonus || 0} LS)
                      </div>
                    )}
                  </div>
                  <div className="bg-purple-50 rounded p-2">
                    <div className="text-purple-600 text-xs">Extra (Charity etc.)</div>
                    <div className="font-semibold text-purple-700">+{allowance.extra_days_total} days</div>
                  </div>
                  <div className="bg-blue-50 rounded p-2">
                    <div className="text-blue-600 text-xs">Carried Over</div>
                    <div className="font-semibold text-blue-700">+{allowance.carried_over_days} days</div>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-gray-500 text-xs">Total</div>
                    <div className="font-semibold">{allowance.total_allowance} days</div>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-gray-500 text-xs">Used</div>
                    <div className="font-semibold">{allowance.days_used} days</div>
                  </div>
                </div>

                {/* Long Service Info */}
                {allowance.long_service_bonus > 0 && (
                  <div className="mt-2 text-xs text-green-600 bg-green-50 rounded px-2 py-1 inline-block">
                    🎉 Long Service Bonus: +{allowance.long_service_bonus} day{allowance.long_service_bonus !== 1 ? 's' : ''}
                  </div>
                )}
                
                {/* Extra Allowances List */}
                {allowance.extra_allowances && allowance.extra_allowances.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs text-gray-500 mb-2">Extra Allowances:</div>
                    <div className="space-y-1">
                      {allowance.extra_allowances.map(extra => (
                        <div key={extra.id} className="flex items-center justify-between text-sm bg-purple-50 rounded px-2 py-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-purple-700">+{extra.days} day{extra.days !== 1 ? 's' : ''}</span>
                            <span className="text-gray-600">- {extra.reason}</span>
                            <span className="text-xs text-gray-400 capitalize">({extra.type})</span>
                          </div>
                          <button
                            onClick={() => handleRemoveExtraAllowance(allowance.year, extra.id)}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Year-End Summary if processed */}
                {allowance.year_end_status === 'processed' && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs text-gray-500 mb-2">Year-End Decision:</div>
                    <div className="flex gap-4 text-sm">
                      {allowance.days_to_carry > 0 && (
                        <span className="text-blue-600">Carried: {allowance.days_to_carry} days</span>
                      )}
                      {allowance.days_to_pay > 0 && (
                        <span className="text-green-600">Paid: {allowance.days_to_pay} days</span>
                      )}
                      {allowance.days_forfeited > 0 && (
                        <span className="text-red-600">Forfeited: {allowance.days_forfeited} days</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Box Section - Conditional Display */}
      {showViewBox && (
        <div id="view-box-section" className="bg-white rounded-xl shadow-md p-6 mt-6">
          {/* Dynamic Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-teal-800 flex items-center">
              {viewMode === 'signin' && (
                <>
                  <Clock className="w-6 h-6 mr-2" />
                  Sign In Records
                </>
              )}
              {viewMode === 'incidents' && (
                <>
                  <FileWarning className="w-6 h-6 mr-2" />
                  Incidents
                </>
              )}
              {viewMode === 'attendance' && (
                <>
                  <ClipboardList className="w-6 h-6 mr-2" />
                  Attendance
                </>
              )}
              {viewMode === 'documents' && (
                <>
                  <FileText className="w-6 h-6 mr-2" />
                  Documents
                </>
              )}
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowViewBox(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              Close
            </Button>
          </div>

        {/* Sign In Records View */}
        {viewMode === 'signin' && (
          <>
            {loadingRecords ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading records...</p>
          </div>
        ) : signInRecords.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>No sign-in records found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {signInRecords.map((record) => {
              const clockInTime = record.clock_in ? new Date(record.clock_in).toLocaleString('en-GB') : 'N/A';
              const clockOutTime = record.clock_out ? new Date(record.clock_out).toLocaleString('en-GB') : 'Still clocked in';
              
              // Calculate duration
              let duration = 'N/A';
              if (record.clock_in && record.clock_out) {
                const start = new Date(record.clock_in);
                const end = new Date(record.clock_out);
                const hours = Math.floor((end - start) / (1000 * 60 * 60));
                const minutes = Math.floor(((end - start) % (1000 * 60 * 60)) / (1000 * 60));
                duration = `${hours}h ${minutes}m`;
              }

              // Determine border color based on status
              const borderColor = record.flagged 
                ? 'border-red-300' 
                : record.clock_out 
                ? 'border-gray-200' 
                : 'border-green-300';

              return (
                <div
                  key={record.id}
                  className={`border-2 ${borderColor} rounded-lg p-4 hover:shadow-md transition-all`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {new Date(record.date).toLocaleDateString('en-GB', { 
                          weekday: 'short', 
                          day: 'numeric', 
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                      <p className="text-xs text-gray-600 flex items-center mt-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        {record.location}
                      </p>
                    </div>
                    {!record.clock_out && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        ACTIVE
                      </span>
                    )}
                    {record.flagged && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                        FLAGGED
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="bg-blue-50 rounded p-2">
                      <p className="text-xs text-blue-600">Clock In</p>
                      <p className="text-sm font-medium text-gray-900">{clockInTime}</p>
                    </div>
                    <div className="bg-purple-50 rounded p-2">
                      <p className="text-xs text-purple-600">Clock Out</p>
                      <p className="text-sm font-medium text-gray-900">{clockOutTime}</p>
                    </div>
                    <div className="bg-green-50 rounded p-2">
                      <p className="text-xs text-green-600">Duration</p>
                      <p className="text-sm font-medium text-gray-900">{duration}</p>
                    </div>
                  </div>

                  {record.note && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-600">Note:</p>
                      <p className="text-xs text-gray-800 italic line-clamp-2">"{record.note}"</p>
                    </div>
                  )}

                  {record.flag_reason && (
                    <div className="mt-3 pt-3 border-t border-red-200">
                      <p className="text-xs text-red-600">Flag:</p>
                      <p className="text-xs text-red-800 line-clamp-2">{record.flag_reason}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
          </>
        )}

        {/* Incidents View */}
        {viewMode === 'incidents' && (
          <>
            {loadingIncidents ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading incidents...</p>
              </div>
            ) : incidents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileWarning className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No incidents logged</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {incidents.map((incident) => {
                  const statusColor = incident.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
                  const categoryColors = {
                    'Disciplinary': 'bg-red-50 border-red-200',
                    'Performance': 'bg-blue-50 border-blue-200',
                    'Attendance': 'bg-purple-50 border-purple-200',
                    'Health and Safety': 'bg-orange-50 border-orange-200',
                    'Other': 'bg-gray-50 border-gray-200'
                  };

                  return (
                    <div
                      key={incident.id}
                      className={`border-2 rounded-lg p-4 hover:shadow-md transition-all ${categoryColors[incident.category] || categoryColors['Other']}`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <span className={`px-2 py-1 ${statusColor} text-xs font-medium rounded-full`}>
                          {incident.status.toUpperCase()}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                          {incident.category}
                        </span>
                      </div>

                      <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                        {incident.short_summary}
                      </h3>

                      <div className="space-y-2 text-sm text-gray-600 mb-3">
                        <p className="line-clamp-2">
                          <strong>Outcome:</strong> {incident.outcome_summary}
                        </p>
                        <p className="text-xs text-gray-500">
                          <strong>Logged by:</strong> {incident.logged_by}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(incident.created_at).toLocaleString('en-GB')}
                        </p>
                      </div>

                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => handleViewIncidentDetails(incident)}
                        >
                          View Full
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => handleResolveIncident(incident.id)}
                        >
                          {incident.status === 'resolved' ? 'Unresolve' : 'Resolve'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteIncident(incident.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Attendance View - RTW Records */}
        {viewMode === 'attendance' && (
          <>
            {loadingRtwRecords ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading RTW records...</p>
              </div>
            ) : rtwRecords.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ClipboardList className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold mb-2">No RTW Records</h3>
                <p>No completed Return to Work forms found for this employee</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Absence Type</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">First Day Absent</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Return Date</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Days</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Completed By</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rtwRecords.map((record, idx) => (
                      <tr 
                        key={record.id} 
                        className="border-b border-gray-100 hover:bg-purple-50 transition-colors"
                        data-testid={`rtw-record-${idx}`}
                      >
                        <td className="py-4 px-4">
                          <span className="inline-block px-2 py-1 bg-pink-100 text-pink-700 rounded text-sm font-medium">
                            {record.absence_type_name || 'Sickness'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-gray-900">
                          {new Date(record.first_day_absent).toLocaleDateString('en-GB')}
                        </td>
                        <td className="py-4 px-4 text-gray-900">
                          {record.return_to_work_date 
                            ? new Date(record.return_to_work_date).toLocaleDateString('en-GB')
                            : 'N/A'}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">
                            {record.total_days || 0}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-gray-700 text-sm">
                          {record.rtw_manager_name || 'N/A'}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Button
                            onClick={() => {
                              setSelectedRtwRecord(record);
                              setShowViewRtwDialog(true);
                            }}
                            size="sm"
                            variant="outline"
                            className="border-purple-500 text-purple-600 hover:bg-purple-50"
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Documents View */}
        {viewMode === 'documents' && (
          <>
            {/* Category Tabs */}
            <div className="mb-6">
              <div className="flex space-x-2 border-b border-gray-200 overflow-x-auto">
                {[
                  { id: 'employment_essentials', label: 'Employment Essentials' },
                  { id: 'performance_conduct', label: 'Performance & Conduct' },
                  { id: 'financial', label: 'Pay, Expenses & Financial' },
                  { id: 'training_certification', label: 'Training & Certification' },
                  { id: 'documents_uploads', label: 'Documents & Uploads' }
                ].map((cat) => {
                  let count = 0;
                  if (cat.id === 'employment_essentials') {
                    count = documents.filter(d => ['contracts', 'handbook', 'policies', 'new_starter_form', 'emergency_contacts', 'medical_allergies', 'Contract', 'Handbook', 'Policies'].includes(d.category)).length;
                  } else if (cat.id === 'performance_conduct') {
                    count = documents.filter(d => ['PIP', 'hr_reports', 'rtw_forms', 'QFM', 'HR Reports', 'RTW Forms'].includes(d.category)).length;
                  } else if (cat.id === 'financial') {
                    count = documents.filter(d => ['payslips', 'expenses', 'wages_advance', 'company_car_mileage', 'Payslips'].includes(d.category)).length;
                  } else if (cat.id === 'training_certification') {
                    count = documents.filter(d => d.category === 'training').length;
                  } else if (cat.id === 'documents_uploads') {
                    count = documents.filter(d => ['miscellaneous', 'other'].includes(d.category)).length;
                  }
                  
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setDocumentCategory(cat.id);
                        if (cat.id === 'employment_essentials') {
                          setEmploymentSubTab('contracts');
                        } else if (cat.id === 'performance_conduct') {
                          setPerformanceSubTab('PIP');
                        } else if (cat.id === 'financial') {
                          setFinancialSubTab('payslips');
                        } else if (cat.id === 'training_certification') {
                          setTrainingSubTab('training');
                        } else if (cat.id === 'documents_uploads') {
                          setDocumentsSubTab('miscellaneous');
                        }
                      }}
                      className={`px-4 py-2 font-medium whitespace-nowrap transition-all ${
                        documentCategory === cat.id
                          ? 'text-teal-600 border-b-2 border-teal-600'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {cat.label} {count > 0 && `(${count})`}
                    </button>
                  );
                })}
              </div>

              {/* Employment Essentials Sub-tabs */}
              {documentCategory === 'employment_essentials' && (
                <div className="flex space-x-2 mt-2 border-b border-gray-200 bg-gray-50 px-4 py-2 overflow-x-auto">
                  {[
                    { id: 'contracts', label: 'Contract' },
                    { id: 'handbook', label: 'Handbook' },
                    { id: 'policies', label: 'Policies' },
                    { id: 'new_starter_form', label: 'New Starter Form' },
                    { id: 'emergency_contacts', label: 'Emergency Contacts' },
                    { id: 'medical_allergies', label: 'Medical & Allergies' }
                  ].map((subCat) => {
                    const count = documents.filter(d => 
                      d.category === subCat.id || 
                      (subCat.id === 'contracts' && d.category === 'Contract') ||
                      (subCat.id === 'handbook' && d.category === 'Handbook') ||
                      (subCat.id === 'policies' && d.category === 'Policies')
                    ).length;
                    return (
                      <button
                        key={subCat.id}
                        onClick={() => setEmploymentSubTab(subCat.id)}
                        className={`px-3 py-1 text-sm whitespace-nowrap transition-all ${
                          employmentSubTab === subCat.id
                            ? 'text-teal-600 font-medium border-b-2 border-teal-600 bg-white'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        {subCat.label} {count > 0 && `(${count})`}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Performance & Conduct Sub-tabs */}
              {documentCategory === 'performance_conduct' && (
                <div className="flex space-x-2 mt-2 border-b border-gray-200 bg-gray-50 px-4 py-2 overflow-x-auto">
                  {[
                    { id: 'PIP', label: 'PIP' },
                    { id: 'hr_reports', label: 'HR Reports' },
                    { id: 'rtw_forms', label: 'RTW Forms' },
                    { id: 'QFM', label: 'QFM' }
                  ].map((subCat) => {
                    const count = documents.filter(d => 
                      d.category === subCat.id ||
                      (subCat.id === 'hr_reports' && d.category === 'HR Reports') ||
                      (subCat.id === 'rtw_forms' && d.category === 'RTW Forms')
                    ).length;
                    return (
                      <button
                        key={subCat.id}
                        onClick={() => setPerformanceSubTab(subCat.id)}
                        className={`px-3 py-1 text-sm whitespace-nowrap transition-all ${
                          performanceSubTab === subCat.id
                            ? 'text-teal-600 font-medium border-b-2 border-teal-600 bg-white'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        {subCat.label} {count > 0 && `(${count})`}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Pay, Expenses & Financial Sub-tabs */}
              {documentCategory === 'financial' && (
                <div className="flex space-x-2 mt-2 border-b border-gray-200 bg-gray-50 px-4 py-2 overflow-x-auto">
                  {[
                    { id: 'payslips', label: 'Payslips' },
                    { id: 'expenses', label: 'Expenses & Mileage' },
                    { id: 'wages_advance', label: 'Wages Advance' },
                    { id: 'company_car_mileage', label: 'Company Car Mileage' }
                  ].map((subCat) => {
                    const count = documents.filter(d => 
                      d.category === subCat.id || (subCat.id === 'payslips' && d.category === 'Payslips')
                    ).length;
                    return (
                      <button
                        key={subCat.id}
                        onClick={() => setFinancialSubTab(subCat.id)}
                        className={`px-3 py-1 text-sm whitespace-nowrap transition-all ${
                          financialSubTab === subCat.id
                            ? 'text-teal-600 font-medium border-b-2 border-teal-600 bg-white'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        {subCat.label} {count > 0 && `(${count})`}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Training & Certification Sub-tabs */}
              {documentCategory === 'training_certification' && (
                <div className="flex space-x-2 mt-2 border-b border-gray-200 bg-gray-50 px-4 py-2 overflow-x-auto">
                  {[
                    { id: 'training', label: 'Training Certificates' }
                  ].map((subCat) => {
                    const count = documents.filter(d => d.category === subCat.id).length;
                    return (
                      <button
                        key={subCat.id}
                        onClick={() => setTrainingSubTab(subCat.id)}
                        className={`px-3 py-1 text-sm whitespace-nowrap transition-all ${
                          trainingSubTab === subCat.id
                            ? 'text-teal-600 font-medium border-b-2 border-teal-600 bg-white'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        {subCat.label} {count > 0 && `(${count})`}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Documents & Uploads Sub-tabs */}
              {documentCategory === 'documents_uploads' && (
                <div className="flex space-x-2 mt-2 border-b border-gray-200 bg-gray-50 px-4 py-2 overflow-x-auto">
                  {[
                    { id: 'miscellaneous', label: 'Miscellaneous Uploads' },
                    { id: 'other', label: 'Other Supporting Documents' }
                  ].map((subCat) => {
                    const count = documents.filter(d => d.category === subCat.id).length;
                    return (
                      <button
                        key={subCat.id}
                        onClick={() => setDocumentsSubTab(subCat.id)}
                        className={`px-3 py-1 text-sm whitespace-nowrap transition-all ${
                          documentsSubTab === subCat.id
                            ? 'text-teal-600 font-medium border-b-2 border-teal-600 bg-white'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        {subCat.label} {count > 0 && `(${count})`}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {loadingDocuments ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading documents...</p>
              </div>
            ) : (() => {
                // Filter documents based on category
                let filteredDocs = [];
                if (documentCategory === 'employment_essentials') {
                  filteredDocs = documents.filter(d => 
                    d.category === employmentSubTab ||
                    (employmentSubTab === 'contracts' && d.category === 'Contract') ||
                    (employmentSubTab === 'handbook' && d.category === 'Handbook') ||
                    (employmentSubTab === 'policies' && d.category === 'Policies')
                  );
                } else if (documentCategory === 'performance_conduct') {
                  filteredDocs = documents.filter(d => 
                    d.category === performanceSubTab ||
                    (performanceSubTab === 'hr_reports' && d.category === 'HR Reports') ||
                    (performanceSubTab === 'rtw_forms' && d.category === 'RTW Forms')
                  );
                } else if (documentCategory === 'financial') {
                  filteredDocs = documents.filter(d => 
                    d.category === financialSubTab ||
                    (financialSubTab === 'payslips' && d.category === 'Payslips')
                  );
                } else if (documentCategory === 'training_certification') {
                  filteredDocs = documents.filter(d => d.category === trainingSubTab);
                } else if (documentCategory === 'documents_uploads') {
                  filteredDocs = documents.filter(d => d.category === documentsSubTab);
                } else {
                  filteredDocs = documents.filter(d => d.category === documentCategory);
                }
                return filteredDocs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>No documents in this category</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="border-2 border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start mb-3">
                      <FileText className="w-8 h-8 text-teal-600 mr-3" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 line-clamp-2">{doc.filename}</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {doc.file_type.toUpperCase()} • {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                    </div>

                    <div className="text-xs text-gray-600 mb-3">
                      <p>Category: {(() => {
                        const categoryMap = {
                          'personal': 'Personal Documents',
                          'new_starter_form': 'New Starter Form',
                          'emergency_contacts': 'Emergency Contacts',
                          'medical_allergies': 'Medical & Allergies',
                          'payslips': 'Payslips',
                          'contracts': 'Contracts',
                          'training': 'Training',
                          'expenses': 'Expenses & Mileage',
                          'wages_advance': 'Wages Advance',
                          'company_car_mileage': 'Company Car Mileage',
                          'other': 'Other'
                        };
                        return categoryMap[doc.category] || doc.category;
                      })()}</p>
                      <p>Uploaded by: {doc.uploaded_by}</p>
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => handleViewDocument(doc)}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                      >
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteDocument(doc.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                    ))}
                  </div>
                );
              })()
            }
          </>
        )}
        </div>
      )}

      {/* Edit Employee Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Employee Settings</DialogTitle>
            <DialogDescription>
              Update employee information and settings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Profile Image Upload */}
            <div>
              <Label htmlFor="profile_image">Profile Image</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white text-2xl font-bold">
                  {profileImagePreview || employee.profile_image ? (
                    <img 
                      src={profileImagePreview || employee.profile_image} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>{employee.first_name?.[0]}{employee.last_name?.[0]}</span>
                  )}
                </div>
                <div>
                  <Input
                    id="profile_image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setProfileImage(file);
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setProfileImagePreview(reader.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload a profile picture (JPG, PNG, max 5MB)</p>
                </div>
              </div>
            </div>

            {/* First Name & Last Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Date of Birth */}
            <div>
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                className="mt-1"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email">Personal Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Used for Xero integration and payslips</p>
            </div>

            {/* Work Email */}
            <div>
              <Label htmlFor="work_email">Work Email</Label>
              <Input
                id="work_email"
                type="email"
                value={formData.work_email || ''}
                onChange={(e) => setFormData({...formData, work_email: e.target.value})}
                className="mt-1"
                placeholder="work@reuseabox.co.uk"
              />
              <p className="text-xs text-gray-500 mt-1">Used for notifications and email digests</p>
            </div>

            {/* Annual Leave Allowance */}
            <div>
              <Label htmlFor="holiday_entitlement_days">Annual Leave Allowance (Days)</Label>
              <Input
                id="holiday_entitlement_days"
                type="number"
                step="0.5"
                min="0"
                placeholder="e.g., 28 or 14.5"
                value={formData.holiday_entitlement_days}
                onChange={(e) => setFormData({...formData, holiday_entitlement_days: parseFloat(e.target.value) || 0})}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Can include half days (e.g., 14.5)</p>
            </div>

            {/* Contracted Hours Per Week */}
            <div>
              <Label htmlFor="contracted_hours_per_week">Contracted Hours Per Week</Label>
              <Input
                id="contracted_hours_per_week"
                type="number"
                step="0.5"
                min="0"
                max="168"
                placeholder="e.g., 40"
                value={formData.contracted_hours_per_week || ''}
                onChange={(e) => setFormData({...formData, contracted_hours_per_week: parseFloat(e.target.value) || 40})}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Used to calculate overtime (default: 40 hours)</p>
            </div>

            {/* Contracted Start & Finish Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="default_start_time">Contracted Start Time</Label>
                <Input
                  id="default_start_time"
                  type="time"
                  value={formData.default_start_time}
                  onChange={(e) => setFormData({...formData, default_start_time: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="default_finish_time">Contracted Finish Time</Label>
                <Input
                  id="default_finish_time"
                  type="time"
                  value={formData.default_finish_time}
                  onChange={(e) => setFormData({...formData, default_finish_time: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Department & Job Title */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="department">Department</Label>
                <select
                  id="department"
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {DEPARTMENTS.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="job_title">Job Title</Label>
                <Input
                  id="job_title"
                  value={formData.job_title}
                  onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Clock In/Out PIN */}
            <div>
              <Label htmlFor="pin">Clock In/Out PIN (4 digits)</Label>
              <Input
                id="pin"
                type="text"
                maxLength="4"
                placeholder="1234"
                value={formData.pin}
                onChange={(e) => setFormData({...formData, pin: e.target.value.replace(/\D/g, '')})}
                className="mt-1"
              />
            </div>

            {/* Manager Checkbox */}
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="is_manager"
                checked={formData.is_manager}
                onChange={(e) => setFormData({...formData, is_manager: e.target.checked})}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <Label htmlFor="is_manager" className="cursor-pointer font-semibold">This person is a manager</Label>
            </div>

            {/* Managed Departments - Show when manager checkbox is ticked */}
            {formData.is_manager && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <Label className="font-semibold mb-3 block">Which departments does this manager oversee?</Label>
                <div className="space-y-2">
                  {DEPARTMENTS.map(dept => (
                    <div key={dept} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`dept-${dept}`}
                        checked={formData.managed_departments.includes(dept)}
                        onChange={() => toggleDepartment(dept)}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <Label htmlFor={`dept-${dept}`} className="cursor-pointer">{dept}</Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowEditDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleUpdateEmployee} className="bg-teal-600 hover:bg-teal-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book Time Off Dialog */}
      <Dialog open={showBookTimeOffDialog} onOpenChange={setShowBookTimeOffDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Book Time Off for {employee?.first_name} {employee?.last_name}</DialogTitle>
            <DialogDescription>
              Book holiday time off for this employee.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Employee Info */}
            <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-teal-600">Holiday Allowance Remaining</p>
                  <p className="text-2xl font-bold text-teal-900">{employee?.holiday_entitlement_days || 0} days</p>
                </div>
                <Calendar className="w-8 h-8 text-teal-600" />
              </div>
            </div>

            {/* First Day */}
            <div>
              <Label htmlFor="first_day">First Day</Label>
              <Input
                id="first_day"
                type="date"
                value={timeOffData.first_day}
                onChange={(e) => setTimeOffData({...timeOffData, first_day: e.target.value})}
                className="mt-1"
              />
              <select
                value={timeOffData.first_day_type}
                onChange={(e) => setTimeOffData({...timeOffData, first_day_type: e.target.value})}
                className="mt-2 w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="full_day">Full Day</option>
                <option value="half_day">Half Day</option>
                <option value="quarter_day">Quarter Day</option>
              </select>
            </div>

            {/* Last Day */}
            <div>
              <Label htmlFor="last_day">Last Day</Label>
              <Input
                id="last_day"
                type="date"
                value={timeOffData.last_day}
                onChange={(e) => setTimeOffData({...timeOffData, last_day: e.target.value})}
                className="mt-1"
              />
              <select
                value={timeOffData.last_day_type}
                onChange={(e) => setTimeOffData({...timeOffData, last_day_type: e.target.value})}
                className="mt-2 w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="full_day">Full Day</option>
                <option value="half_day">Half Day</option>
                <option value="quarter_day">Quarter Day</option>
              </select>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <textarea
                id="notes"
                value={timeOffData.notes}
                onChange={(e) => setTimeOffData({...timeOffData, notes: e.target.value})}
                placeholder="Reason for time off..."
                rows={3}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This booking will be automatically approved and will immediately deduct from the employee's allowance.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowBookTimeOffDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleBookTimeOff} className="bg-teal-600 hover:bg-teal-700">
              Book Time Off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Register Sick Dialog */}
      <Dialog open={showRegisterSickDialog} onOpenChange={setShowRegisterSickDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Register Sickness for {employee?.first_name} {employee?.last_name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Employee Info */}
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-red-600">Sickness Record</p>
                  <p className="text-lg font-semibold text-red-900">
                    This will be used for Bradford Factor calculation
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="sick_date">Date</Label>
              <Input
                id="sick_date"
                type="date"
                value={sicknessData.date}
                onChange={(e) => setSicknessData({...sicknessData, date: e.target.value})}
                className="mt-1"
              />
            </div>

            {/* Absence Type */}
            <div>
              <Label htmlFor="absence_type">Sickness Type</Label>
              <select
                id="absence_type"
                value={sicknessData.absence_type_code}
                onChange={(e) => setSicknessData({...sicknessData, absence_type_code: e.target.value})}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              >
                <optgroup label="Sickness">
                  {absenceTypes
                    .filter(t => t.category === 'Sick' && !t.code.startsWith('SH') && !t.code.startsWith('G'))
                    .map(type => (
                      <option key={type.code} value={type.code}>
                        {type.code} - {type.name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Sickness Holiday (Deducts from allowance)">
                  {absenceTypes
                    .filter(t => t.code.startsWith('SH'))
                    .map(type => (
                      <option key={type.code} value={type.code}>
                        {type.code} - {type.name} ({type.days_deducted} days)
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Grace Days">
                  {absenceTypes
                    .filter(t => t.code.startsWith('G'))
                    .map(type => (
                      <option key={type.code} value={type.code}>
                        {type.code} - {type.name}
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="sick_notes">Notes (Optional)</Label>
              <textarea
                id="sick_notes"
                value={sicknessData.notes}
                onChange={(e) => setSicknessData({...sicknessData, notes: e.target.value})}
                placeholder="Reason for sickness, symptoms, etc..."
                rows={3}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Info Message */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> 
                {sicknessData.absence_type_code.startsWith('SH') 
                  ? ' This is a sickness holiday type and WILL deduct from the employee\'s holiday allowance.'
                  : ' Regular sickness types (S, S1, S2) do NOT deduct from holiday allowance but will affect Bradford Factor.'}
              </p>
            </div>

            {employee?.holiday_entitlement_days !== undefined && (
              <div className="bg-teal-50 rounded-lg p-3 border border-teal-200">
                <p className="text-sm text-teal-600">Current Holiday Allowance</p>
                <p className="text-xl font-bold text-teal-900">{employee.holiday_entitlement_days} days</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowRegisterSickDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleRegisterSick} className="bg-red-600 hover:bg-red-700">
              Register Sickness
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Register Absent Dialog */}
      <Dialog open={showRegisterAbsentDialog} onOpenChange={setShowRegisterAbsentDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Register Absence for {employee?.first_name} {employee?.last_name}</DialogTitle>
            <DialogDescription>
              Record an authorised absence for this employee.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Warning Banner */}
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-orange-600">Absence Record</p>
                  <p className="text-lg font-semibold text-orange-900">
                    Will appear on calendar and reports
                  </p>
                </div>
                <FileWarning className="w-8 h-8 text-orange-600" />
              </div>
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="absent_date">Date</Label>
              <Input
                id="absent_date"
                type="date"
                value={absentData.date}
                onChange={(e) => setAbsentData({...absentData, date: e.target.value})}
                className="mt-1"
              />
            </div>

            {/* Absence Type */}
            <div>
              <Label htmlFor="absent_type">Absence Type</Label>
              <select
                id="absent_type"
                value={absentData.absence_type_code}
                onChange={(e) => setAbsentData({...absentData, absence_type_code: e.target.value})}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              >
                {absenceTypes
                  .filter(t => t.category === 'Absent')
                  .map(type => (
                    <option key={type.code} value={type.code}>
                      {type.code} - {type.name}
                      {type.days_deducted > 0 ? ` (Deducts ${type.days_deducted} days)` : ''}
                    </option>
                  ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="absent_notes">Notes (Optional)</Label>
              <textarea
                id="absent_notes"
                value={absentData.notes}
                onChange={(e) => setAbsentData({...absentData, notes: e.target.value})}
                placeholder="Reason for absence..."
                rows={3}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> {
                  absenceTypes.find(t => t.code === absentData.absence_type_code)?.days_deducted > 0
                    ? ' This absence type WILL deduct from the employee\'s holiday allowance.'
                    : ' This absence type does NOT deduct from holiday allowance.'
                }
              </p>
            </div>

            {/* Type-specific descriptions */}
            {absentData.absence_type_code === 'UA' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>Unauthorised Absence:</strong> Employee did not have permission to be absent.
                </p>
              </div>
            )}

            {absentData.absence_type_code === 'AA' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>Authorised Absence:</strong> Employee had permission but not using holiday allowance.
                </p>
              </div>
            )}

            {employee?.holiday_entitlement_days !== undefined && (
              <div className="bg-teal-50 rounded-lg p-3 border border-teal-200">
                <p className="text-sm text-teal-600">Current Holiday Allowance</p>
                <p className="text-xl font-bold text-teal-900">{employee.holiday_entitlement_days} days</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowRegisterAbsentDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleRegisterAbsent} className="bg-orange-600 hover:bg-orange-700">
              Register Absence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book Other Leave Dialog */}
      <Dialog open={showBookOtherLeaveDialog} onOpenChange={setShowBookOtherLeaveDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Book Other Leave for {employee?.first_name} {employee?.last_name}</DialogTitle>
            <DialogDescription>
              Record other types of leave for this employee.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Info Banner */}
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-purple-600">Special Leave</p>
                  <p className="text-lg font-semibold text-purple-900">
                    For compassionate, training, and other special circumstances
                  </p>
                </div>
                <Briefcase className="w-8 h-8 text-purple-600" />
              </div>
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="leave_date">Date</Label>
              <Input
                id="leave_date"
                type="date"
                value={otherLeaveData.date}
                onChange={(e) => setOtherLeaveData({...otherLeaveData, date: e.target.value})}
                className="mt-1"
              />
            </div>

            {/* Leave Type */}
            <div>
              <Label htmlFor="leave_type">Leave Type</Label>
              <select
                id="leave_type"
                data-testid="other-leave-type-select"
                value={otherLeaveData.absence_type_code}
                onChange={(e) => setOtherLeaveData({...otherLeaveData, absence_type_code: e.target.value})}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              >
                <optgroup label="Work-Related">
                  {absenceTypes
                    .filter(t => ['W', 'W1', 'W2', 'T', 'PF', 'V', 'SV'].includes(t.code))
                    .map(type => (
                      <option key={type.code} value={type.code}>
                        {type.code} - {type.name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Personal Leave">
                  {absenceTypes
                    .filter(t => ['C', 'M', 'PL', 'BL', 'DA', 'DOC'].includes(t.code))
                    .map(type => (
                      <option key={type.code} value={type.code}>
                        {type.code} - {type.name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Absence Types">
                  {absenceTypes
                    .filter(t => ['AA', 'UA'].includes(t.code))
                    .map(type => (
                      <option key={type.code} value={type.code}>
                        {type.code} - {type.name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Grace Days">
                  {absenceTypes
                    .filter(t => ['G', 'G1', 'G2'].includes(t.code))
                    .map(type => (
                      <option key={type.code} value={type.code}>
                        {type.code} - {type.name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Public Holidays">
                  {absenceTypes
                    .filter(t => t.code === 'B')
                    .map(type => (
                      <option key={type.code} value={type.code}>
                        {type.code} - {type.name}
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="leave_notes">Notes (Optional)</Label>
              <textarea
                id="leave_notes"
                value={otherLeaveData.notes}
                onChange={(e) => setOtherLeaveData({...otherLeaveData, notes: e.target.value})}
                placeholder="Reason or additional details..."
                rows={3}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Dynamic Leave Type Descriptions */}
            {otherLeaveData.absence_type_code === 'C' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Compassionate Leave:</strong> For family emergencies or difficult personal circumstances.
                </p>
              </div>
            )}

            {otherLeaveData.absence_type_code === 'BL' && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-800">
                  <strong>Bereavement Leave:</strong> Time off following the death of a family member or close friend.
                </p>
              </div>
            )}

            {['M', 'PL'].includes(otherLeaveData.absence_type_code) && (
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                <p className="text-sm text-pink-800">
                  <strong>Maternity/Paternity/Parental Leave:</strong> Statutory leave for new parents.
                </p>
              </div>
            )}

            {['W', 'W1', 'W2'].includes(otherLeaveData.absence_type_code) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>Work from Home:</strong> Employee working remotely rather than from office.
                </p>
              </div>
            )}

            {otherLeaveData.absence_type_code === 'T' && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <p className="text-sm text-indigo-800">
                  <strong>Training:</strong> Attending courses, workshops, or professional development.
                </p>
              </div>
            )}

            {otherLeaveData.absence_type_code === 'V' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Volunteering:</strong> Company-sponsored volunteer activities.
                </p>
              </div>
            )}

            {otherLeaveData.absence_type_code === 'B' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>Bank Holiday:</strong> Public holiday - no work expected.
                </p>
              </div>
            )}

            {['G', 'G1', 'G2'].includes(otherLeaveData.absence_type_code) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Grace Day:</strong> A discretionary day off granted by management.
                </p>
              </div>
            )}

            {otherLeaveData.absence_type_code === 'AA' && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                <p className="text-sm text-teal-800">
                  <strong>Authorised Absence:</strong> Pre-approved time off for reasons other than holiday or sickness.
                </p>
              </div>
            )}

            {otherLeaveData.absence_type_code === 'UA' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>Unauthorised Absence:</strong> Unplanned absence without prior approval. This may trigger HR review.
                </p>
              </div>
            )}

            {/* General Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> {
                  absenceTypes.find(t => t.code === otherLeaveData.absence_type_code)?.days_deducted > 0
                    ? ' This leave type WILL deduct from the employee\'s holiday allowance.'
                    : ' This leave type does NOT deduct from holiday allowance.'
                }
              </p>
            </div>

            {employee?.holiday_entitlement_days !== undefined && (
              <div className="bg-teal-50 rounded-lg p-3 border border-teal-200">
                <p className="text-sm text-teal-600">Current Holiday Allowance</p>
                <p className="text-xl font-bold text-teal-900">{employee.holiday_entitlement_days} days</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowBookOtherLeaveDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleBookOtherLeave} className="bg-purple-600 hover:bg-purple-700">
              Book Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Incident Dialog */}
      <Dialog open={showLogIncidentDialog} onOpenChange={setShowLogIncidentDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Log HR Incident - {employee?.first_name} {employee?.last_name}</DialogTitle>
            <DialogDescription>
              Record an HR incident for this employee. All fields marked with * are required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Category */}
            <div>
              <Label htmlFor="incident_category">Category *</Label>
              <select
                id="incident_category"
                value={incidentData.category}
                onChange={(e) => setIncidentData({...incidentData, category: e.target.value})}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="Disciplinary">Disciplinary</option>
                <option value="Performance">Performance</option>
                <option value="Attendance">Attendance</option>
                <option value="Health and Safety">Health and Safety</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Short Summary */}
            <div>
              <Label htmlFor="short_summary">Short Summary *</Label>
              <Input
                id="short_summary"
                value={incidentData.short_summary}
                onChange={(e) => setIncidentData({...incidentData, short_summary: e.target.value})}
                placeholder="Brief description of the incident"
                className="mt-1"
              />
            </div>

            {/* Detailed Explanation */}
            <div>
              <Label htmlFor="detailed_explanation">Detailed Explanation *</Label>
              <textarea
                id="detailed_explanation"
                value={incidentData.detailed_explanation}
                onChange={(e) => setIncidentData({...incidentData, detailed_explanation: e.target.value})}
                placeholder="Full details of what happened..."
                rows={4}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="incident_date">Incident Date *</Label>
                <Input
                  id="incident_date"
                  type="date"
                  value={incidentData.incident_date}
                  onChange={(e) => setIncidentData({...incidentData, incident_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="incident_time">Incident Time *</Label>
                <Input
                  id="incident_time"
                  type="time"
                  value={incidentData.incident_time}
                  onChange={(e) => setIncidentData({...incidentData, incident_time: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Managers Involved */}
            <div>
              <Label>Managers Involved</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded p-2">
                {managers.map((manager) => (
                  <label key={manager.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={incidentData.managers_involved.includes(manager.email)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setIncidentData({
                            ...incidentData,
                            managers_involved: [...incidentData.managers_involved, manager.email]
                          });
                        } else {
                          setIncidentData({
                            ...incidentData,
                            managers_involved: incidentData.managers_involved.filter(email => email !== manager.email)
                          });
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{manager.first_name} {manager.last_name} ({manager.email})</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Outcome/Action Summary */}
            <div>
              <Label htmlFor="outcome_summary">Outcome/Action Summary *</Label>
              <Input
                id="outcome_summary"
                value={incidentData.outcome_summary}
                onChange={(e) => setIncidentData({...incidentData, outcome_summary: e.target.value})}
                placeholder="Brief summary of actions taken (e.g., 'Verbal warning issued')"
                className="mt-1"
              />
            </div>

            {/* Outcome/Actions Taken - Detailed */}
            <div>
              <Label htmlFor="outcome_actions">Outcome/Actions Taken (Detailed)</Label>
              <textarea
                id="outcome_actions"
                value={incidentData.outcome_actions}
                onChange={(e) => setIncidentData({...incidentData, outcome_actions: e.target.value})}
                placeholder="Detailed description of actions taken and expected outcomes..."
                rows={3}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Attach File */}
            <div>
              <Label htmlFor="attachment_file">Attachment (Optional)</Label>
              <Input
                id="attachment_file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setIncidentData({...incidentData, attachment_file: file});
                  }
                }}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {incidentData.attachment_file 
                  ? `Selected: ${incidentData.attachment_file.name}` 
                  : 'PDF, images, or Office documents (max 10MB)'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowLogIncidentDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleLogIncident} className="bg-red-600 hover:bg-red-700">
              Log Incident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Incident Details Dialog */}
      <Dialog open={showIncidentDetailsDialog} onOpenChange={setShowIncidentDetailsDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Incident Details</DialogTitle>
            <DialogDescription>
              View detailed information about this incident.
            </DialogDescription>
          </DialogHeader>

          {selectedIncident && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <span className={`px-3 py-1 ${selectedIncident.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} text-xs font-medium rounded-full`}>
                    {selectedIncident.status.toUpperCase()}
                  </span>
                  <span className="ml-2 px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                    {selectedIncident.category}
                  </span>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Short Summary</h3>
                <p className="text-gray-900">{selectedIncident.short_summary}</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Detailed Explanation</h3>
                <p className="text-gray-900 whitespace-pre-wrap">{selectedIncident.detailed_explanation}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-700">Incident Date</h3>
                  <p className="text-gray-900">{new Date(selectedIncident.incident_date).toLocaleDateString('en-GB')}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700">Incident Time</h3>
                  <p className="text-gray-900">{selectedIncident.incident_time}</p>
                </div>
              </div>

              {selectedIncident.managers_involved && selectedIncident.managers_involved.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-700">Managers Involved</h3>
                  <ul className="list-disc list-inside text-gray-900">
                    {selectedIncident.managers_involved.map((email, idx) => (
                      <li key={idx}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h3 className="font-semibold text-gray-700">Outcome/Action Summary</h3>
                <p className="text-gray-900">{selectedIncident.outcome_summary}</p>
              </div>

              {selectedIncident.outcome_actions && (
                <div>
                  <h3 className="font-semibold text-gray-700">Outcome/Actions Taken (Detailed)</h3>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedIncident.outcome_actions}</p>
                </div>
              )}

              {selectedIncident.attachment_url && (
                <div>
                  <h3 className="font-semibold text-gray-700">Attachment</h3>
                  <a href={selectedIncident.attachment_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    View Attachment
                  </a>
                </div>
              )}

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <strong>Logged by:</strong> {selectedIncident.logged_by}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Date:</strong> {new Date(selectedIncident.created_at).toLocaleString('en-GB')}
                </p>
                {selectedIncident.resolved_at && (
                  <p className="text-sm text-gray-600">
                    <strong>Resolved:</strong> {new Date(selectedIncident.resolved_at).toLocaleString('en-GB')}
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowIncidentDetailsDialog(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDocumentDialog} onOpenChange={setShowUploadDocumentDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload Document - {employee?.first_name} {employee?.last_name}</DialogTitle>
            <DialogDescription>
              Upload a document for this employee.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Category Selection */}
            <div>
              <Label htmlFor="upload_category">Document Category *</Label>
              <select
                id="upload_category"
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              >
                <optgroup label="Employment Essentials">
                  <option value="contracts">Contract</option>
                  <option value="handbook">Handbook</option>
                  <option value="policies">Policies</option>
                  <option value="new_starter_form">New Starter Form</option>
                  <option value="emergency_contacts">Emergency Contacts</option>
                  <option value="medical_allergies">Medical & Allergies</option>
                </optgroup>
                <optgroup label="Performance & Conduct">
                  <option value="PIP">PIP (Performance Improvement Plan)</option>
                  <option value="hr_reports">HR Reports</option>
                  <option value="rtw_forms">RTW Forms (Return to Work)</option>
                  <option value="QFM">QFM</option>
                </optgroup>
                <optgroup label="Pay, Expenses & Financial">
                  <option value="payslips">Payslips</option>
                  <option value="expenses">Expenses & Mileage</option>
                  <option value="wages_advance">Wages Advance</option>
                  <option value="company_car_mileage">Company Car Mileage</option>
                </optgroup>
                <optgroup label="Training & Certification">
                  <option value="training">Training Certificates</option>
                </optgroup>
                <optgroup label="Documents & Uploads">
                  <option value="miscellaneous">Miscellaneous Uploads</option>
                  <option value="other">Other Supporting Documents</option>
                </optgroup>
              </select>
            </div>

            {/* File Upload */}
            <div>
              <Label htmlFor="file_upload">Select File *</Label>
              <input
                id="file_upload"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">Supported formats: PDF, JPG, PNG</p>
            </div>

            {selectedFile && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Selected:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => {
              setShowUploadDocumentDialog(false);
              setSelectedFile(null);
            }} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleUploadDocument} className="bg-teal-600 hover:bg-teal-700">
              Upload Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog open={showViewDocumentDialog} onOpenChange={setShowViewDocumentDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>View Document</DialogTitle>
            <DialogDescription>
              View document details and download if needed.
            </DialogDescription>
          </DialogHeader>

          {selectedDocument && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900">{selectedDocument.filename}</h3>
                <p className="text-sm text-gray-600">
                  Category: {selectedDocument.category} • Type: {selectedDocument.file_type.toUpperCase()}
                </p>
                <p className="text-xs text-gray-500">
                  Uploaded by {selectedDocument.uploaded_by} on {new Date(selectedDocument.uploaded_at).toLocaleString('en-GB')}
                </p>
              </div>

              {/* Preview */}
              <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                {selectedDocument.file_type === 'pdf' ? (
                  <div className="text-center py-8">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600">PDF Preview</p>
                    <p className="text-sm text-gray-500 mt-2">Click download to view the full document</p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600">Image Preview</p>
                    <p className="text-sm text-gray-500 mt-2">Click download to view the full image</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowViewDocumentDialog(false)} variant="outline">
              Close
            </Button>
            {selectedDocument && (
              <Button 
                onClick={() => handleDownloadDocument(selectedDocument.id, selectedDocument.filename)}
                className="bg-teal-600 hover:bg-teal-700"
              >
                Download
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Return to Work Dialog */}
      <Dialog open={showReturnToWorkDialog} onOpenChange={setShowReturnToWorkDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Return to Work - {employee?.first_name} {employee?.last_name}</DialogTitle>
            <DialogDescription>
              Complete the return to work interview form for this employee's absence.
            </DialogDescription>
          </DialogHeader>

          {ongoingAbsence && (
            <div className="space-y-4">
              {/* First Day Info */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm font-semibold text-blue-900 mb-2">Absence Details</p>
                <p className="text-sm text-blue-700">
                  <strong>Type:</strong> {ongoingAbsence.absence_type_name || 'Sickness'}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>First day absent:</strong> {new Date(ongoingAbsence.first_day_absent).toLocaleDateString('en-GB')}
                </p>
                {ongoingAbsence.notes && (
                  <p className="text-xs text-blue-600 mt-2 italic">Note: {ongoingAbsence.notes}</p>
                )}
              </div>

              {/* Last Day Absent — drives the per-day list below */}
              <div>
                <Label htmlFor="rtw_date">Last Day Absent *</Label>
                <Input
                  id="rtw_date"
                  type="date"
                  value={rtwFormData.return_to_work_date}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRtwFormData({ ...rtwFormData, return_to_work_date: v });
                    fetchRtwPreview(v);
                  }}
                  min={ongoingAbsence.first_day_absent}
                  className="mt-1"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">The final day the employee was absent</p>
              </div>

              {/* Per-day breakdown */}
              {rtwFormData.return_to_work_date && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-gray-800">Day-by-day breakdown</p>
                    {rtwDailyEntries.length > 0 && (
                      <p className="text-xs text-gray-600">
                        {rtwDailyEntries.length} working day(s) • {' '}
                        <strong>
                          {rtwDailyEntries.reduce((s, e) => s + (Number(e.days_deducted) || 0), 0).toFixed(2)}
                        </strong>{' '}
                        day(s) deducted from allowance
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Weekends and bank holidays are skipped. Each day defaults to the original code —
                    change individual days to record half-days or other sickness types.
                  </p>

                  {rtwPreviewLoading && (
                    <p className="text-sm text-gray-600 italic">Loading days…</p>
                  )}
                  {rtwPreviewError && (
                    <p className="text-sm text-red-600">{rtwPreviewError}</p>
                  )}
                  {!rtwPreviewLoading && !rtwPreviewError && rtwDailyEntries.length === 0 && (
                    <p className="text-sm text-gray-600 italic">
                      No working days in this period — try a later last-day-absent.
                    </p>
                  )}

                  {rtwDailyEntries.length > 0 && (
                    <div className="space-y-2">
                      {rtwDailyEntries.map((entry) => {
                        const dt = new Date(entry.date);
                        const weekday = dt.toLocaleDateString('en-GB', { weekday: 'short' });
                        const label = `${weekday} ${dt.toLocaleDateString('en-GB')}`;
                        return (
                          <div
                            key={entry.date}
                            className="flex items-center justify-between gap-3 bg-white rounded-md p-2 border border-gray-200"
                            data-testid={`rtw-day-${entry.date}`}
                          >
                            <span className="text-sm text-gray-800 font-medium min-w-[120px]">{label}</span>
                            <select
                              value={entry.absence_type_code}
                              onChange={(e) => updateRtwDailyEntry(entry.date, e.target.value)}
                              className="flex-1 text-sm border rounded-md px-2 py-1 bg-white"
                            >
                              {rtwEligibleTypes.map(t => (
                                <option key={t.code} value={t.code}>
                                  {t.code} — {t.name}
                                  {Number(t.days_deducted) > 0 ? ` (deducts ${t.days_deducted})` : ''}
                                </option>
                              ))}
                            </select>
                            <span
                              className={`text-xs min-w-[80px] text-right ${
                                Number(entry.days_deducted) > 0 ? 'text-orange-700 font-semibold' : 'text-gray-500'
                              }`}
                            >
                              {Number(entry.days_deducted) > 0
                                ? `−${Number(entry.days_deducted).toFixed(2)} day`
                                : 'no deduction'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* RTW Reason */}
              <div>
                <Label htmlFor="rtw_reason">State briefly why you were off sick *</Label>
                <textarea
                  id="rtw_reason"
                  value={rtwFormData.rtw_reason}
                  onChange={(e) => setRtwFormData({ ...rtwFormData, rtw_reason: e.target.value })}
                  className="w-full mt-1 p-2 border rounded-md min-h-[80px]"
                  placeholder="Brief description..."
                />
              </div>

              {/* Manager Name (Auto-filled) & RTW Discussion Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Manager's Name</Label>
                  <Input
                    value={ongoingAbsence.rtw_manager_name || 'Auto-filled'}
                    disabled
                    className="mt-1 bg-gray-100"
                  />
                </div>
                <div>
                  <Label htmlFor="rtw_discussion_date">RTW Discussion Date *</Label>
                  <Input
                    id="rtw_discussion_date"
                    type="date"
                    value={rtwFormData.rtw_discussion_date}
                    onChange={(e) => setRtwFormData({ ...rtwFormData, rtw_discussion_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Medical Certification */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="medical_cert"
                  checked={rtwFormData.medical_certification}
                  onChange={(e) => setRtwFormData({ ...rtwFormData, medical_certification: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="medical_cert" className="cursor-pointer">
                  Has the necessary medical certification been presented? (e.g., fit note)
                </Label>
              </div>

              {/* Discussion Summary */}
              <div>
                <Label htmlFor="discussion_summary">Summary of discussion *</Label>
                <textarea
                  id="discussion_summary"
                  value={rtwFormData.discussion_summary}
                  onChange={(e) => setRtwFormData({ ...rtwFormData, discussion_summary: e.target.value })}
                  className="w-full mt-1 p-2 border rounded-md min-h-[100px]"
                  placeholder="Key points discussed during return to work meeting..."
                />
              </div>

              {/* Other Comments */}
              <div>
                <Label htmlFor="other_comments">Any other comments</Label>
                <textarea
                  id="other_comments"
                  value={rtwFormData.other_comments}
                  onChange={(e) => setRtwFormData({ ...rtwFormData, other_comments: e.target.value })}
                  className="w-full mt-1 p-2 border rounded-md min-h-[80px]"
                  placeholder="Optional additional notes..."
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowReturnToWorkDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleReturnToWork} className="bg-green-600 hover:bg-green-700">
              Complete Return to Work
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* View RTW Record Dialog */}
      <Dialog open={showViewRtwDialog} onOpenChange={setShowViewRtwDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Return to Work Record</DialogTitle>
          </DialogHeader>

          {selectedRtwRecord && (
            <div className="space-y-4">
              {/* Absence Summary */}
              <div className="bg-pink-50 rounded-lg p-4 border border-pink-200">
                <h3 className="font-semibold text-pink-900 mb-3">Absence Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-pink-700 font-medium">Absence Type:</p>
                    <p className="text-pink-900">
                      {selectedRtwRecord.rtw_changes_summary
                        || selectedRtwRecord.absence_type_name
                        || 'Sickness'}
                    </p>
                  </div>
                  <div>
                    <p className="text-pink-700 font-medium">Total Days:</p>
                    <p className="text-pink-900 font-semibold text-lg">{selectedRtwRecord.total_days || 0} days</p>
                  </div>
                  <div>
                    <p className="text-pink-700 font-medium">First Day Absent:</p>
                    <p className="text-pink-900">{new Date(selectedRtwRecord.first_day_absent).toLocaleDateString('en-GB')}</p>
                  </div>
                  <div>
                    <p className="text-pink-700 font-medium">Return to Work Date:</p>
                    <p className="text-pink-900">{selectedRtwRecord.return_to_work_date
                      ? new Date(selectedRtwRecord.return_to_work_date).toLocaleDateString('en-GB')
                      : 'N/A'}</p>
                  </div>
                  {(selectedRtwRecord.total_allowance_deduction || 0) > 0 && (
                    <div className="col-span-2">
                      <p className="text-pink-700 font-medium">Deducted from annual leave:</p>
                      <p className="text-pink-900 font-semibold">
                        {Number(selectedRtwRecord.total_allowance_deduction).toFixed(2)} day(s)
                      </p>
                    </div>
                  )}
                </div>
                {selectedRtwRecord.notes && (
                  <div className="mt-3 pt-3 border-t border-pink-200">
                    <p className="text-pink-700 font-medium text-sm">Initial Notes:</p>
                    <p className="text-pink-900 text-sm italic mt-1">{selectedRtwRecord.notes}</p>
                  </div>
                )}
                {Array.isArray(selectedRtwRecord.daily_entries) && selectedRtwRecord.daily_entries.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-pink-200">
                    <p className="text-pink-700 font-medium text-sm mb-2">Day-by-day breakdown:</p>
                    <ul className="space-y-1 text-sm">
                      {selectedRtwRecord.daily_entries.map(e => (
                        <li key={e.date} className="flex justify-between text-pink-900">
                          <span>
                            {new Date(e.date).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                          </span>
                          <span>
                            {e.absence_type_code}
                            {Number(e.days_deducted) > 0
                              ? ` (−${Number(e.days_deducted).toFixed(2)})`
                              : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* RTW Meeting Details */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-3">Return to Work Meeting</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-blue-700 font-medium">Manager:</p>
                    <p className="text-blue-900">{selectedRtwRecord.rtw_manager_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-blue-700 font-medium">Discussion Date:</p>
                    <p className="text-blue-900">{selectedRtwRecord.rtw_discussion_date 
                      ? new Date(selectedRtwRecord.rtw_discussion_date).toLocaleDateString('en-GB')
                      : 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-blue-700 font-medium">Medical Certification Presented:</p>
                    <p className="text-blue-900 font-semibold">
                      {selectedRtwRecord.medical_certification ? '✓ Yes' : '✗ No'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reason for Absence */}
              {selectedRtwRecord.rtw_reason && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Reason for Absence</h3>
                  <p className="text-gray-700 text-sm">{selectedRtwRecord.rtw_reason}</p>
                </div>
              )}

              {/* Discussion Summary */}
              {selectedRtwRecord.discussion_summary && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h3 className="font-semibold text-green-900 mb-2">Discussion Summary</h3>
                  <p className="text-green-800 text-sm whitespace-pre-wrap">{selectedRtwRecord.discussion_summary}</p>
                </div>
              )}

              {/* Other Comments */}
              {selectedRtwRecord.other_comments && (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <h3 className="font-semibold text-amber-900 mb-2">Additional Comments</h3>
                  <p className="text-amber-800 text-sm whitespace-pre-wrap">{selectedRtwRecord.other_comments}</p>
                </div>
              )}

              {/* Metadata */}
              <div className="bg-gray-100 rounded-lg p-3 text-xs text-gray-600">
                <p>
                  <strong>RTW Completed By:</strong> {selectedRtwRecord.rtw_completed_by || 'N/A'}
                </p>
                {selectedRtwRecord.rtw_completed_at && (
                  <p className="mt-1">
                    <strong>Completed At:</strong> {new Date(selectedRtwRecord.rtw_completed_at).toLocaleString('en-GB')}
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowViewRtwDialog(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Generate Report Dialog */}
      <Dialog open={showGenerateReportDialog} onOpenChange={setShowGenerateReportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Employee Report</DialogTitle>
            <DialogDescription>
              Generate a comprehensive PDF report including incidents and attendance data.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Date Range</Label>
              <select
                value={reportDateRange}
                onChange={(e) => setReportDateRange(e.target.value)}
                className="w-full mt-1 p-2 border rounded-lg"
              >
                <option value="week_to_date">Week to Date</option>
                <option value="month_to_date">Month to Date</option>
                <option value="year_to_date">Year to Date</option>
                <option value="all_time">All Time</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {reportDateRange === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={customReportDates.start}
                    onChange={(e) => setCustomReportDates({ ...customReportDates, start: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={customReportDates.end}
                    onChange={(e) => setCustomReportDates({ ...customReportDates, end: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateReportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateReport} 
              disabled={generatingReport}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {generatingReport ? 'Generating...' : 'Generate Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Extra Allowance Dialog */}
      <Dialog open={showAddExtraDialog} onOpenChange={setShowAddExtraDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Add Extra Leave Days</DialogTitle>
            <DialogDescription>
              Add extra days for charity work, long service, bonuses, etc.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="extra_year">Year</Label>
              <select
                id="extra_year"
                value={selectedAllowanceYear}
                onChange={(e) => setSelectedAllowanceYear(parseInt(e.target.value))}
                className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="extra_type">Type</Label>
              <select
                id="extra_type"
                value={extraAllowanceData.type}
                onChange={(e) => setExtraAllowanceData({...extraAllowanceData, type: e.target.value})}
                className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="charity">Charity Work</option>
                <option value="long_service">Long Service Award</option>
                <option value="bonus">Bonus</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="extra_days">Number of Days</Label>
              <Input
                id="extra_days"
                type="number"
                step="0.5"
                min="0.5"
                max="30"
                value={extraAllowanceData.days}
                onChange={(e) => setExtraAllowanceData({...extraAllowanceData, days: parseFloat(e.target.value) || 1})}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="extra_reason">Reason</Label>
              <Input
                id="extra_reason"
                placeholder="e.g., Beach cleanup volunteer day"
                value={extraAllowanceData.reason}
                onChange={(e) => setExtraAllowanceData({...extraAllowanceData, reason: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddExtraDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddExtraAllowance} className="bg-teal-600 hover:bg-teal-700">
              Add Extra Days
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </div>
  );
};

export default EmployeeProfile;
